import type {ReactElement} from 'react';
import {useEffect, useState} from 'react';
import {InputField} from '@Components/input-field-v2';
import type {InputFieldControls, InputType} from '@Components/input-field-v2/input-field.types';
import {InputControlItemType, InputFieldState, InputFieldType} from '@Components/input-field-v2/input-field.types';
import {Button, IconPlacement, ButtonSize, ButtonType} from '@Components/button';
import {ClickableDiv} from '@Components/clickable-div';
import {Text, TextSize} from '@Components/text';
import {EventWizardErrorInfo} from '@Components/event-wizard/event-wizard-error-info';
import {useEventWizardShouldCheckErrState, useEventWizardTicketingConfig, useEventWizardTicketingFees} from '@Components/event-wizard/event-wizard.hooks';
import {
  onEventWizardTicketingFeeParamsLoaded,
  onEventWizardTicketingMaxPerOrderChange,
  onEventWizardTicketingPriceChange,
  onEventWizardTicketingTaxChange,
  onEventWizardTicketingTotalTicketsChange,
} from '@Components/event-wizard/event-wizard-slice';
import {fetchTicketingFeeParams} from '@Components/event-wizard/event-wizard-ticketing/ticketing-fees.helper';
import {openTicketingAddTaxModal} from '@Components/modals/ticketing-add-tax-modal/ticketing-add-tax-modal.library';
import {doesPlanDeclareLimit, getPlanLimitAllowanceMessage, openPlanLimitUpgradeDialog} from '@Libraries/event-ticketing.library';
import {formatExactNumberInUSD} from '@Utils/number.util';
import {
  clampMaxPerOrderToTotal,
  isTicketingMaxPerOrderValid,
  isTicketingPriceValid,
  isTicketingTotalTicketsValid,
  shouldShowTicketingPassFeesToggle,
} from '@Components/event-wizard/event-wizard-ticketing/event-wizard-ticketing.helper';
import {TicketingPassFeesToggle} from '@Components/event-wizard/event-wizard-ticketing/components/ticketing-pass-fees-toggle/ticketing-pass-fees-toggle';
import {
  DEFAULT_MAX_PER_ORDER,
  MAX_PER_ORDER,
  MAX_PRICE,
  MAX_TOTAL_TICKETS,
  MIN_PRICE,
  POSITIVE_WHOLE_NUMBER_INPUT_PATTERN,
  PRICE_INPUT_PATTERN,
  TaxMode,
} from '@Components/event-wizard/event-wizard-ticketing/event-wizard-ticketing.types';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {useAppDispatch} from '@/hooks';
import styles from './ticketing-ticket-details.module.scss';
import {PRICE_CURRENCY_CONTROL_ID, PRICE_CURRENCY_SUFFIX} from './ticketing-ticket-details.types';

export function TicketingTicketDetails(): ReactElement {
  const dispatch = useAppDispatch();
  const config = useEventWizardTicketingConfig();
  const ticketingFees = useEventWizardTicketingFees();
  const shouldCheckForErr = useEventWizardShouldCheckErrState();
  const [priceInput, setPriceInput] = useState<string>(() => (config.price > 0 ? String(config.price) : ''));

  useEffect(() => {
    // No cleanup: the dispatch targets the global store (unmount-safe), and dropping the
    // response would only force a refetch on the next mount.
    if (ticketingFees === null) {
      void fetchTicketingFeeParams()
        .then((feeParams): void => {
          dispatch(onEventWizardTicketingFeeParamsLoaded(feeParams));
        })
        .catch((): void => {
          // Display-only data: the price hint falls back to its static copy; remounting the step retries.
        });
    }
  }, []);

  const isPriceInError = shouldCheckForErr && !isTicketingPriceValid(config.price);
  const isTotalInError = shouldCheckForErr && !isTicketingTotalTicketsValid(config.totalTickets);
  const isMaxPerOrderInError = shouldCheckForErr && !isTicketingMaxPerOrderValid(config.maxPerOrder, config.totalTickets);

  const getPriceCurrencySuffix = (): InputFieldControls => {
    return {
      showDefaultControls: false,
      customControls: [
        {
          id: PRICE_CURRENCY_CONTROL_ID,
          type: InputControlItemType.INACTIVE_TEXT,
          text: PRICE_CURRENCY_SUFFIX,
          containerClassName: 'spacing-p-r-1'
        },
      ],
    };
  };

  const onPriceChange = (value: InputType): void => {
    const raw = value.toString();
    if (!PRICE_INPUT_PATTERN.test(raw)) {
      return; // reject the keystroke (letters, extra dots, 3+ decimals) and keep the prior text
    }
    setPriceInput(raw);
    const parsed = Number(raw);
    dispatch(onEventWizardTicketingPriceChange(Number.isFinite(parsed) ? parsed : 0));
  };

  const onTotalTicketsChange = (value: InputType): void => {
    const raw = value.toString();
    if (!POSITIVE_WHOLE_NUMBER_INPUT_PATTERN.test(raw)) {
      return; // reject the keystroke (letters, 'e', signs, decimal points) and keep the prior count
    }
    const numeric = Number(raw);
    dispatch(onEventWizardTicketingTotalTicketsChange(numeric >= 1 ? numeric : null));
  };

  const onMaxPerOrderChange = (value: InputType): void => {
    const raw = value.toString();
    if (!POSITIVE_WHOLE_NUMBER_INPUT_PATTERN.test(raw)) {
      return; // reject the keystroke (letters, 'e', signs, decimal points) and keep the prior count
    }
    const numeric = Number(raw);
    dispatch(onEventWizardTicketingMaxPerOrderChange(numeric >= 1 ? numeric : null));
  };

  const onMaxPerOrderBlur = (value: InputType): void => {
    // Leaving the field empty fills in the default rather than blocking the step — capped at the total, or on a
    // smaller event the default would block it again.
    if (Number(value) < 1) {
      dispatch(onEventWizardTicketingMaxPerOrderChange(clampMaxPerOrderToTotal(DEFAULT_MAX_PER_ORDER, config.totalTickets)));
    }
  };

  /**
   * Settling the total decides how large an order may be, so the pair is reconciled here rather than blocking the
   * step over a max per order the organiser never entered. On blur, not on change: typing "50" passes through "5".
   * An unlimited or not-yet-whole total has nothing to clamp to, and clamping to a fractional one would move the
   * error onto a field that was fine.
   */
  const onTotalTicketsBlur = (): void => {
    const {maxPerOrder, totalTickets} = config;
    if (maxPerOrder === null || !Number.isInteger(totalTickets)) {
      return;
    }
    dispatch(onEventWizardTicketingMaxPerOrderChange(clampMaxPerOrderToTotal(maxPerOrder, totalTickets)));
  };

  /**
   * Each bound states the condition it tests, and free is the default rather than a fallthrough: a price with no
   * recognised problem has nothing specific to say, and "enter a price" is both true and actionable. That also
   * catches the one value no comparison catches — every comparison with `NaN` is false — and matches
   * `onPriceChange`, which already resolves an unparseable entry to 0. A rule added to `isTicketingPriceValid`
   * later therefore cannot silently inherit the maximum-price sentence.
   */
  const getPriceHintText = (): string => {
    if (config.price > MAX_PRICE) {
      return window.i18next.t('pmwjs_event_ticketing_max_price_hint', {amount: formatExactNumberInUSD(MAX_PRICE)});
    }
    if (config.price > 0 && config.price < MIN_PRICE) {
      return window.i18next.t('pmwjs_event_ticketing_min_price_hint', {amount: formatExactNumberInUSD(MIN_PRICE)});
    }
    return window.i18next.t('pmwjs_event_ticketing_price_free_hint');
  };

  /**
   * The one line under the price field. It disappears entirely once the price is a valid paid amount, which needs
   * no explanation; below the minimum it states the floor, and the same sentence escalates from neutral to error
   * styling once the organiser hits Next. Built here rather than passed to InputField's `information` because that
   * renders text with no icon, and both states are icon-led.
   */
  const getPriceHint = (): ReactElement | null => {
    if (config.price >= MIN_PRICE && !isPriceInError) {
      return null;
    }

    const colorClass = isPriceInError ? 'ct-error-primary' : 'ct-neutral-secondary';

    return (
      <div className="flex-row-align-center align-items-start spacing-m-t-2 gap-1">
        <Icon className={colorClass} icon={isPriceInError ? 'icon-exclamation-triangle' : 'icon-info'} size={IconSize.SIZE_ICON_16} />
        <Text className={colorClass} val={getPriceHintText()} size={TextSize.SM} />
      </div>
    );
  };

  /**
   * States the ceiling that actually applied, which is the total when one is set and `MAX_PER_ORDER` when inventory
   * is unlimited (`isTicketingMaxPerOrderValid`). Naming "total tickets" unconditionally sent an organiser with
   * unlimited inventory to go and fix a field reading "Unlimited".
   */
  const getMaxPerOrderCrossFieldError = (): ReactElement | null => {
    if (!isMaxPerOrderInError) {
      return null;
    }
    const cap = config.totalTickets ?? MAX_PER_ORDER;
    return (
      <EventWizardErrorInfo
        className="gap-1 align-items-start spacing-m-t-2"
        text={window.i18next.t('pmwjs_event_ticketing_err_max_per_order', {max: cap.toLocaleString()})}
      />
    );
  };

  /**
   * What the organiser's plan allows, under the fields that decide how many tickets they can sell. Stated
   * plainly and always, for whatever plan they are on — at setup time nothing has happened yet, so this is
   * information, not a warning. How much of the allowance they have actually used is the dashboard banner's
   * job, and only that surface reads the state.
   *
   * The link is the usual brand teal here; the banner's is its own text colour. Two different surfaces,
   * deliberately not sharing a link style.
   */
  const getPlanLimitHint = (): ReactElement | null => {
    if (ticketingFees === null || !doesPlanDeclareLimit(ticketingFees.planLimit)) {
      return null;
    }

    return (
      <div className="flex-row-align-center align-items-start spacing-m-t-2 gap-1 flex-wrap">
        <Icon className="ct-neutral-secondary flex-shrink-0" icon="icon-info" size={IconSize.SIZE_ICON_16} />
        <Text className="ct-neutral-secondary" val={getPlanLimitAllowanceMessage(ticketingFees.planLimit)} size={TextSize.SM} />
        <ClickableDiv className="flexbox _cursor-pointer" onClick={openPlanLimitUpgradeDialog}>
          <Text className="ct-brand-primary" val={window.i18next.t('pmwjs_upgrade_plan_to_remove_limits')} size={TextSize.SM} bold underline />
        </ClickableDiv>
      </div>
    );
  };

  const onRemoveTax = (): void => {
    dispatch(onEventWizardTicketingTaxChange({taxLabel: '', taxRatePercent: 0, taxMode: TaxMode.EXCLUSIVE}));
  };

  const getTaxUnsetRow = (): ReactElement => {
    return (
      <div className={`${styles.taxRow} gap-4 flex-row-align-center flex-justify-between bg-neutral-alt-primary-default corner-radius-8 _full-width`}>
        <div className="gap-2 flex-row-align-center">
          <Icon className="ct-neutral-primary flex-shrink-0" icon="icon-info" size={IconSize.SIZE_ICON_16} />
          <Text className="ct-neutral-primary" val={window.i18next.t('pmwjs_event_ticketing_add_tax_if_applicable')} size={TextSize.SM} />
        </div>
        <Button
          type={ButtonType.SECONDARY}
          size={ButtonSize.SMALL}
          icon="icon-discount"
          iconPlacement={IconPlacement.LEFT}
          text={window.i18next.t('pmwjs_event_ticketing_add_tax')}
          onClick={openTicketingAddTaxModal}
        />
      </div>
    );
  };

  const getTaxSetRow = (): ReactElement => {
    return (
      <div className={`${styles.taxRow} gap-4 flex-row-align-center flex-justify-between bg-neutral-alt-primary-default corner-radius-8 _full-width`}>
        <Text
          className="ct-neutral-primary"
          val={window.i18next.t('pmwjs_event_ticketing_tax_summary', {label: config.taxLabel, rate: config.taxRatePercent})}
          size={TextSize.SM}
        />
        <div className="gap-1 flex-row-align-center flex-shrink-0">
          <Button
            type={ButtonType.TERTIARY}
            size={ButtonSize.SMALL}
            icon="icon-pencil"
            iconPlacement={IconPlacement.LEFT}
            text={window.i18next.t('pmwjs_edit')}
            onClick={openTicketingAddTaxModal}
          />
          <Button type={ButtonType.TERTIARY} size={ButtonSize.SMALL} icon="icon-close" iconPlacement={IconPlacement.LEFT} text={window.i18next.t('pmwjs_remove')} onClick={onRemoveTax} />
        </div>
      </div>
    );
  };

  // Tax applies to paid tickets only, so it is gated on price > 0. Before a tax is set the row prompts "Add tax";
  // once set it summarises "{label}: {rate}%" with Edit / Remove. All input lives in the Add-tax modal, which is
  // the sole writer of tax_label / tax_rate / tax_mode via onEventWizardTicketingTaxChange (see the UI↔engine contract).
  const getTaxControl = (): ReactElement | null => {
    if (config.price <= 0) {
      return null;
    }
    const hasTax = config.taxLabel.trim() !== '' && config.taxRatePercent > 0;
    return hasTax ? getTaxSetRow() : getTaxUnsetRow();
  };

  return (
    <div className="gap-4 flex-column-align-start _full-width">
      <div className="flex-column-align-start _full-width">
        <Text val={window.i18next.t('pmwjs_event_ticket_details')} size={TextSize.LG} bold />
        <Text className="ct-neutral-secondary" val={window.i18next.t('pmwjs_event_setup_pricing_availability_tickets')} size={TextSize.SM} />
      </div>

      <div className="flex-column-align-start _full-width">
        <div className="gap-3 flex-row-align-center align-items-start _full-width">
          <div className={`${styles.priceInputContainer} flex-column-align-start flex-1`}>
            <InputField
              className={`_full-width ${isPriceInError ? 'js-event-wizard-error' : ''}`}
              inputClassName="corner-radius-8"
              input={priceInput}
              onInputChange={onPriceChange}
              label={window.i18next.t('pmwjs_event_ticketing_ticket_price')}
              inputType={InputFieldType.TEXT}
              placeholder={window.i18next.t('pmwjs_free')}
              // The suffix would otherwise sit next to the "Free" placeholder and contradict it, so it appears
              // only once the organiser has typed something. The hint below carries the message in every state.
              controls={priceInput === '' ? undefined : getPriceCurrencySuffix()}
              state={isPriceInError ? InputFieldState.ERROR : InputFieldState.DEFAULT}
            />
          </div>
          <TicketingPassFeesToggle isHidden={!shouldShowTicketingPassFeesToggle(config)} className="spacing-m-t-6" />
        </div>
        {getPriceHint()}
      </div>

      <div className="flex-column-align-start _full-width">
        <div className="gap-2 flex-row-align-center align-items-start _full-width">
          <div className="flex-1">
            <InputField
              className={`_full-width ${isTotalInError ? 'js-event-wizard-error' : ''}`}
              inputClassName="corner-radius-8"
              input={config.totalTickets ?? ''}
              onInputChange={onTotalTicketsChange}
              onInputBlur={onTotalTicketsBlur}
              label={window.i18next.t('pmwjs_event_ticketing_total_tickets')}
              placeholder="Unlimited"
              inputType={InputFieldType.TEXT}
              inputMode="numeric"
              state={isTotalInError ? InputFieldState.ERROR : InputFieldState.DEFAULT}
              information={isTotalInError ? window.i18next.t('pmwjs_event_ticketing_err_total_tickets', {max: MAX_TOTAL_TICKETS.toLocaleString()}) : undefined}
            />
          </div>
          <div className="flex-1">
            <InputField
              className="_full-width"
              inputClassName="corner-radius-8"
              input={config.maxPerOrder ?? ''}
              onInputChange={onMaxPerOrderChange}
              onInputBlur={onMaxPerOrderBlur}
              label={window.i18next.t('pmwjs_event_ticketing_max_per_order')}
              placeholder="5"
              inputType={InputFieldType.TEXT}
              inputMode="numeric"
              state={isMaxPerOrderInError ? InputFieldState.ERROR : InputFieldState.DEFAULT}
            />
            {getMaxPerOrderCrossFieldError()}
          </div>
        </div>
        {getPlanLimitHint()}
      </div>

      {getTaxControl()}
    </div>
  );
}
